import type { LeDataImageAttachment } from '~/types/leData';

import { type WandBox, type WandPoint, ringBounds } from './wandGeometry';

const MAX_IMAGE_BYTES = 4_500_000;
const MAX_EDGE = 1400;

function payloadBytes(data: string): number {
  return Math.floor((data.length * 3) / 4);
}

function splitDataUrl(dataUrl: string): LeDataImageAttachment | null {
  const match = /^data:(image\/png|image\/jpeg);base64,(.+)$/.exec(dataUrl);
  if (!match || payloadBytes(match[2]) > MAX_IMAGE_BYTES) {
    return null;
  }
  return { data: match[2], mediaType: match[1] as LeDataImageAttachment['mediaType'] };
}

export async function cropRegionImage(
  root: HTMLElement,
  ring: ReadonlyArray<WandPoint>,
): Promise<LeDataImageAttachment | null> {
  if (ring.length < 3) {
    return null;
  }
  const bounds = paddedBounds(ringBounds(ring), 4);
  const rootRect = root.getBoundingClientRect();
  const localLeft = bounds.left - rootRect.left;
  const localTop = bounds.top - rootRect.top;
  const localWidth = Math.max(1, bounds.right - bounds.left);
  const localHeight = Math.max(1, bounds.bottom - bounds.top);
  if (localWidth < 4 || localHeight < 4) {
    return null;
  }
  try {
    const { default: html2canvas } = await import('html2canvas');
    const captured = await html2canvas(root, {
      backgroundColor: '#ffffff',
      height: localHeight,
      logging: false,
      scale: 1,
      useCORS: true,
      width: localWidth,
      x: localLeft,
      y: localTop,
    });
    const fit = Math.min(1, MAX_EDGE / Math.max(localWidth, localHeight, 1));
    const width = Math.max(1, Math.round(localWidth * fit));
    const height = Math.max(1, Math.round(localHeight * fit));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.save();
    context.beginPath();
    ring.forEach((point, index) => {
      const x = (point.x - bounds.left) * fit;
      const y = (point.y - bounds.top) * fit;
      if (index === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    });
    context.closePath();
    context.clip();
    context.drawImage(captured, 0, 0, width, height);
    context.restore();
    return splitDataUrl(canvas.toDataURL('image/png')) ?? splitDataUrl(canvas.toDataURL('image/jpeg', 0.72));
  } catch {
    return null;
  }
}

function paddedBounds(bounds: WandBox, pad: number): WandBox {
  return {
    left: bounds.left - pad,
    top: bounds.top - pad,
    right: bounds.right + pad,
    bottom: bounds.bottom + pad,
  };
}
